// a library to wrap and simplify api calls
import axios from 'axios'
import contentVersionInterceptor from '../ApiFunctions'

let serverUrlInput = false
if (typeof document !== 'undefined') {
  const serverUrlEl = document.getElementById('apiRootUrl')
  if (serverUrlEl) {
    serverUrlInput = serverUrlEl.value
  }
}
let contentVersion = false
if (typeof document !== 'undefined') {
  const contentVersionEl = document.getElementById('content-version')
  if (contentVersionEl) {
    contentVersion = contentVersionEl.value
  }
}

export const serverUrl = serverUrlInput ? serverUrlInput : API_LINK
export const serverUrlCrm = CRM_API_LINK

export const whitelist = [
  'content/display-names/product/',
  'content/quote-request-validator-resources/product/',
  'content/display-names?calculator',
  'content/contract-draft-resources?calculator',
]

// our "constructor"
export const suri20Api = axios.create({
  baseURL: `${serverUrl}`,
  timeout: 100000,
  headers: {
    'Content-Type': 'application/json',
    'time-zone': 'Europe/Prague',
  },
})

suri20Api.interceptors.request.use(contentVersionInterceptor(contentVersion), (err) => {
  Promise.reject(err)
})

suri20Api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response.status === 401) {
      location.reload()
    }
    return Promise.reject(error)
  },
)

const crmApi = axios.create({
  baseURL: `${serverUrlCrm}/`,
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
  transformRequest: [
    (data, headers) => {
      delete headers.common.Authorization
      delete headers.Authorization
      return JSON.stringify(data)
    },
  ],
})

const setHeader = (api) => (name, value) => {
  if (value) {
    api.defaults.headers.common[name] = value
  } else {
    delete api.defaults.headers.common[name]
  }
}

const getHeader = (name) => axios.defaults.headers.common[name]

const endpoints = (api) => ({
  // post
  quoteAsync: (product, data) => api.post(`quote/${product}/async`, data),
  getQuote: (calculatorName, data) => api.post(`calculator/${calculatorName}/quote`, data),
  calculatorContractDraft: (calculatorName, data) => api.post(`calculator/${calculatorName}/contract/draft`, data),
  acceptContractDraft: (calculatorName, contractId, data) =>
    api.post(`/calculator/${calculatorName}/contract/${contractId}/accept`, data),
  publicOfferSend: (timeout, data) =>
    api.post(`/product/offer/car/public-pdf?template=car/calculation&timeout=${timeout}`, data),
  publicOfferSendViaEmail: (timeout, email, data, productName) =>
    api.post(
      `/product/offer/${productName}/public-pdf-email?template=${productName}/calculation&email=${email}&timeout=${timeout}`,
      data,
    ),
  publicOfferSendViaCrm: (email, data) => api.post(`/sf-public/pdf-calculation-public?email=${email}`, data),
  boOfferSendViaEmail: (email, data, productName) =>
    api.post(`/product/offer/${productName}/bo-pdf-email?template=car/calculation-bo&email=${email}`, data),
  updateOrCreateQuoteRequestLink: (productName, data, link) =>
    api.post(`/link-builder/quoteRequest/${productName}${link ? `/${link}` : ''}`, data),
  updateOrCreateContractDraftLink: (productName, data, link) =>
    api.post(`/link-builder/contractDraft/${productName}${link ? `/${link}` : ''}`, data),
  getProductLink: (data) => api.post(`/link-builder/quoteRequest/car`, data),
  saveProductLink: (data) => crmApi.post('InputForm', data),
  log: (data) => api.post('/uni-logger/write', data),

  // get
  getDefaultContent: () => api.get('content/default-content'),
  getNegotiation: () => api.get('content/contract-draft-negotiation?calculator=_carDefault'),
  allProducts: () => api.get('product/'),
  productContent: (product) => api.get(`content/display-names?product=${product}`),
  productContentBackOffice: (product) => api.get(`content/display-names-back-office?product=${product}`),
  getDefaultValues: (product) => api.get(`content/default-values?product=${product}`),
  productResources: (product) => api.get(`content/quote-request-validator-resources?product=${product}`),
  productStep4Content: (product) => api.get(`content/step-4-content?product=${product}`),
  getQuoteContent: (product) => api.get(`content/quote-display-names?product=${product}`),
  getTariffsContent: (product) => api.get(`content/tariffs-content?product=${product}`),
  getProductDocuments: (product) => api.get(`content/documents?product=${product}`),
  getOneTariffContent: (product, tariffName) =>
    api.get(`content/tariffs-content?product=${product}&path=${tariffName}`),
  getTwoTariffContents: (product, tariffName1, tariffName2) =>
    axios
      .all([
        api.get(`content/tariffs-content?product=${product}&path=${tariffName1}`),
        api.get(`content/tariffs-content?product=${product}&path=${tariffName2}`),
      ])
      .then(axios.spread((res1, res2) => new Promise((resolve) => resolve({ tariff1: res1, tariff2: res2 })))),
  getQuoteFilters: (product) => api.get(`/product/${product}/filters`),
  getDiscountResources: (product) => api.get(`/product/${product}/discount-resources`),
  getDiscountContent: (product) => api.get(`content/discounts-display-names?product=${product}`),
  calculatorSpecification: (calculatorName) => api.get(`calculator/${calculatorName}/specification`),
  calculatorContent: (calculatorName) => api.get(`content/display-names?calculator=${calculatorName}`),
  calculatorContentBackOffice: (calculatorName) =>
    axios
      .all([
        api.get(`content/display-names?calculator=${calculatorName}`),
        api.get(`content/display-names-back-office?calculator=${calculatorName}`),
      ])
      .then(
        axios.spread(
          (res1, res2) => new Promise((resolve) => resolve({ displayNames: res1, displayNamesBackOffice: res2 })),
        ),
      ),
  contactDraftContent: (calculatorName) => api.get(`content/draft-display-names?calculator=${calculatorName}`),
  calculatorStep4Content: (calculatorName) => api.get(`content/step-4-content?calculator=${calculatorName}/`),
  calculatorResources: (calculatorName) => api.get(`content/contract-draft-resources?calculator=${calculatorName}`),
  coveragesNames: (calculatorName) => api.get(`content/coverages-names?calculator=${calculatorName}`),
  getContract: (contractId) => api.get(`contract/${contractId}`),
  getCoverageLimits: (calculatorName) => api.get(`content/coverage-limits?calculator=${calculatorName}`),
  getCoverageLimitsBo: (calculatorName) => api.get(`content/coverage-limits-bo?calculator=${calculatorName}`),
  getAllCoverageLimits: () => api.get(`content/all-coverage-limits?calculator=_carDefault`),
  getAllCoverageLimitsBo: () => api.get(`content/all-coverage-limits-bo?calculator=_carDefault`),
  getPaymentInfo: (contractId) => api.get(`contract/${contractId}/to-pay`),
  getContractData: (linkId) => api.get(`/link-builder/link/${linkId}`),
  getUserMe: () => api.get('user/me?includeOrgDetails=yes'),

  // delete

  // helpers
  serverUrl,
  setHeader: setHeader(api),
  getHeader,
})

export const rawEndpoints = endpoints

export default endpoints(suri20Api)
